import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';

import { isReservedProjectName, slugify } from '../utils';
import { useProjects } from '../services/api/KeynomeAPIHooks';
import {
  useArchiveProject,
  useCreateProject,
  useRestoreProject,
  useUpdateProject,
} from '../services/api/KeynomeAPIMutations';

const projectErrorMessage = (error) => {
  if (error?.statusCode === 400) return 'Project name already exists - please choose a different name';
  if (error?.statusCode === 422) return 'Project name must be at least 5 characters and no more than 50 characters';
  return 'Failed to update projects. Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.';
}

const reservedProjectNameMessage = 'The project name "archive" is reserved for system archive behavior. Please choose a different name.';
const projectCreatedLoadErrorMessage = 'Project created, but we could not load it. Please refresh or select it from the dropdown.';
const projectNameLengthMessage = 'Project name must be at least 5 characters and no more than 50 characters.';
const isValidProjectNameLength = (projectName) => {
  const projectNameLength = projectName?.trim().length || 0;
  return projectNameLength >= 5 && projectNameLength <= 50;
};

function ProjectManagementDialog(props) {
  const { open, onClose, organizationId, activeProject, setActiveProject } = props;
  const navigate = useNavigate();
  const { data: projects = [], isLoading, refetch: refetchProjects } = useProjects(organizationId, { includeInactive: true });
  const [friendlyName, setFriendlyName] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const [projectToArchive, setProjectToArchive] = useState(null);
  const [error, setError] = useState('');

  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();
  const archiveProjectMutation = useArchiveProject();
  const restoreProjectMutation = useRestoreProject();

  const managedProjects = projects.filter(project => project.attributes.name !== 'archive');
  const activeProjects = managedProjects.filter(project => project.attributes.is_active !== false);
  const archivedProjects = managedProjects.filter(project => project.attributes.is_active === false);
  const hasMultipleActiveProjects = activeProjects.length > 1;
  const isMutating = createProjectMutation.isPending || updateProjectMutation.isPending || archiveProjectMutation.isPending || restoreProjectMutation.isPending;
  const isNewProjectNameReserved = isReservedProjectName(newProjectName);
  const isEditingProjectNameReserved = isReservedProjectName(editingProject?.projectName);
  const isNewProjectNameInvalidLength = Boolean(newProjectName) && !isValidProjectNameLength(newProjectName);
  const isEditingProjectNameInvalidLength = Boolean(editingProject?.projectName) && !isValidProjectNameLength(editingProject.projectName);

  const handleCreateNameChange = (event) => {
    const nextFriendlyName = event.target.value;
    setError('');
    setFriendlyName(nextFriendlyName);
    setNewProjectName(slugify(nextFriendlyName));
  };

  const handleEditNameChange = (event) => {
    const nextFriendlyName = event.target.value;
    setError('');
    setEditingProject({
      ...editingProject,
      friendlyName: nextFriendlyName,
      projectName: slugify(nextFriendlyName),
    });
  };

  const handleError = (mutationError) => {
    setError(projectErrorMessage(mutationError));
  };

  const resetCreateForm = () => {
    setFriendlyName('');
    setNewProjectName('');
  };

  const findRefetchedProject = async (projectId) => {
    const updatedProjects = await refetchProjects();
    return updatedProjects.data?.find(project => project.id === projectId);
  };

  const handleCreateProject = () => {
    if (!organizationId || !newProjectName.trim()) return;
    if (isNewProjectNameReserved) {
      setError(reservedProjectNameMessage);
      return;
    }
    if (!isValidProjectNameLength(newProjectName)) {
      setError(projectNameLengthMessage);
      return;
    }

    createProjectMutation.mutate(
      { organizationId, projectName: newProjectName },
      {
        onSuccess: async (createdProject) => {
          try {
            const createdProjectWithFields = await findRefetchedProject(createdProject?.id);
            const projectUrl = createdProjectWithFields?.attributes.keynome_web_url;
            if (!createdProjectWithFields || !projectUrl) {
              setError(projectCreatedLoadErrorMessage);
              return;
            }

            resetCreateForm();
            setError('');
            setActiveProject(createdProjectWithFields);
            const newProjectUrl = new URL(projectUrl);
            navigate(newProjectUrl.pathname);
          } catch {
            setError(projectCreatedLoadErrorMessage);
          }
        },
        onError: handleError,
      }
    );
  };

  const handleEditProject = (project) => {
    setError('');
    setEditingProject({
      id: project.id,
      friendlyName: project.attributes.name,
      projectName: project.attributes.name,
    });
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setError('');
  };

  const handleSaveProject = (project) => {
    if (!editingProject?.projectName.trim()) return;
    if (isEditingProjectNameReserved) {
      setError(reservedProjectNameMessage);
      return;
    }
    if (!isValidProjectNameLength(editingProject.projectName)) {
      setError(projectNameLengthMessage);
      return;
    }

    updateProjectMutation.mutate(
      { projectId: project.id, attributes: { name: editingProject.projectName } },
      {
        onSuccess: async (updatedProject) => {
          if (activeProject?.id === updatedProject?.id) {
            const updatedProjectWithFields = await findRefetchedProject(updatedProject?.id);
            if (updatedProjectWithFields) {
              setActiveProject(updatedProjectWithFields);
              const updatedProjectUrl = new URL(updatedProjectWithFields.attributes.keynome_web_url);
              navigate(updatedProjectUrl.pathname);
            }
          }
          handleCancelEdit();
        },
        onError: (mutationError) => {
          setProjectToArchive(null);
          handleError(mutationError);
        },
      }
    );
  };

  const getActiveManagedProjects = (projectList) => {
    return projectList
      .filter(project => project.attributes.name !== 'archive')
      .filter(project => project.attributes.is_active !== false);
  };

  const handleArchiveConfirm = async () => {
    if (!projectToArchive) return;
    const updatedProjects = await refetchProjects();
    const refetchedActiveProjects = getActiveManagedProjects(updatedProjects.data || []);

    if (refetchedActiveProjects.length <= 1) {
      setProjectToArchive(null);
      setError('You must keep at least one active project. Create or restore another project before archiving this one.');
      return;
    }

    archiveProjectMutation.mutate(
      { projectId: projectToArchive.id },
      {
        onSuccess: () => {
          if (activeProject?.id === projectToArchive.id) {
            const fallbackProject = refetchedActiveProjects.find(project => project.id !== projectToArchive.id);
            if (fallbackProject) {
              const fallbackProjectUrl = new URL(fallbackProject.attributes.keynome_web_url);
              navigate(fallbackProjectUrl.pathname);
            } else {
              navigate('/');
            }
          }
          setProjectToArchive(null);
          setError('');
        },
        onError: handleError,
      }
    );
  };

  const handleRestoreProject = (project) => {
    restoreProjectMutation.mutate(
      { projectId: project.id },
      {
        onSuccess: () => setError(''),
        onError: handleError,
      }
    );
  };

  const handleClose = () => {
    resetCreateForm();
    handleCancelEdit();
    setProjectToArchive(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Manage Projects</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Create project
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 3 }}>
            <TextField
              label="Project Name"
              value={friendlyName}
              onChange={handleCreateNameChange}
              error={isNewProjectNameReserved || isNewProjectNameInvalidLength}
              helperText={isNewProjectNameReserved ? reservedProjectNameMessage : isNewProjectNameInvalidLength ? projectNameLengthMessage : friendlyName ? `Label: ${newProjectName}` : ''}
              fullWidth
              size="small"
            />
            <Button
              onClick={handleCreateProject}
              disabled={!organizationId || !newProjectName.trim() || isNewProjectNameReserved || isNewProjectNameInvalidLength || isMutating}
              variant="contained"
            >
              Create
            </Button>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Active projects
          </Typography>
          {isLoading && <Typography color="text.secondary">Loading projects...</Typography>}
          {!isLoading && activeProjects.length === 0 && <Typography color="text.secondary">No active projects.</Typography>}
          {!isLoading && activeProjects.length === 1 && (
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              This is the only active project. Create or restore another project before archiving it.
            </Typography>
          )}
          {activeProjects.map(project => (
            <Box key={project.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 1 }}>
              {editingProject?.id === project.id ? (
                <>
                  <TextField
                    label="Project Name"
                    value={editingProject.friendlyName}
                    onChange={handleEditNameChange}
                    error={isEditingProjectNameReserved || isEditingProjectNameInvalidLength}
                    helperText={isEditingProjectNameReserved ? reservedProjectNameMessage : isEditingProjectNameInvalidLength ? projectNameLengthMessage : `Label: ${editingProject.projectName}`}
                    fullWidth
                    size="small"
                  />
                  <Button
                    onClick={() => handleSaveProject(project)}
                    disabled={!editingProject.projectName.trim() || isEditingProjectNameReserved || isEditingProjectNameInvalidLength || isMutating}
                    variant="contained"
                  >
                    Save
                  </Button>
                  <Button onClick={handleCancelEdit} disabled={isMutating}>Cancel</Button>
                </>
              ) : (
                <>
                  <Typography sx={{ flexGrow: 1 }}>{project.attributes.name}</Typography>
                  <Button onClick={() => handleEditProject(project)} disabled={isMutating}>Rename</Button>
                  <Button
                    onClick={() => setProjectToArchive(project)}
                    disabled={isMutating || !hasMultipleActiveProjects}
                    color="warning"
                  >
                    Archive
                  </Button>
                </>
              )}
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Archived projects
          </Typography>
          {!isLoading && archivedProjects.length === 0 && <Typography color="text.secondary">No archived projects.</Typography>}
          {archivedProjects.map(project => (
            <Box key={project.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 1 }}>
              <Typography sx={{ flexGrow: 1 }}>{project.attributes.name}</Typography>
              <Button
                onClick={() => handleRestoreProject(project)}
                disabled={isMutating}
                variant="outlined"
              >
                Restore
              </Button>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isMutating}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!projectToArchive} onClose={() => setProjectToArchive(null)}>
        <DialogTitle>Archive Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive {projectToArchive?.attributes.name}? Archived projects are hidden from the dropdown but can be restored later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectToArchive(null)} disabled={archiveProjectMutation.isPending}>Cancel</Button>
          <Button
            onClick={handleArchiveConfirm}
            color="primary"
            variant="contained"
            disabled={archiveProjectMutation.isPending || !hasMultipleActiveProjects}
            aria-label="confirm-archive-project"
          >
            Yes, Archive
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProjectManagementDialog;
